import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tab,
  Tabs,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  GetASAISubDepartments,
  GetCurrentShiftImportRecords,
  GetDepartmentShifts,
  SaveEmployeeShiftOverride,
  GetEmployeeShiftOverrideHistory,
} from "../../../services/asai";

import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";
import PageContainer from "../../../components/container/PageContainer";
import { routes } from "../../../routes/routes";
import { useNavigate } from "react-router-dom";

import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";

type EmployeeType = "payroll" | "contract" | "cnc";

interface ShiftConfig {
  /** Primary key from ShiftMaster. */
  id: string;
  /** Same as id; used as the roster grouping key. */
  key: string;
  label: string;
  shiftType: string;
  employeeType: EmployeeType;
}

interface SubDepartmentConfig {
  /** MachineMaster.Id. */
  id: string;
  key: string;
  label: string;
  code: string;
}

interface ShiftEmployee {
  personId: string;
  personName: string;
  shiftName: string;
  subDepartmentName: string;
  subDepartmentCode: string;
  departmentId: number;
  subDepartmentId: number;
  offDay: string;
  shiftMasterId: string;
  baseShiftMasterId: string;
  baseShiftImportRecordId: number;
  effectiveFrom: string;
  effectiveTo: string;
  lastUpdatedAt: string;
  isTemporaryOverride: boolean;
}

interface ShiftRosterTab {
  id: string;
  label: string;
  count: number;
}

interface ShiftOverrideHistoryRow {
  shiftAssignmentOverrideId?: number;
  baseShiftName?: string;
  overrideShiftName?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  recordStatus?: string;
  changeReason?: string;
  createdAt?: string;
  createdByLogin?: string;
  updatedAt?: string;
  updatedByLogin?: string;
}

const EMPLOYEE_TYPE_OPTIONS: Array<{
  value: EmployeeType;
  label: string;
}> = [
  { value: "payroll", label: "Payroll" },
  { value: "contract", label: "Contract" },
  { value: "cnc", label: "Apprentice" },
];

const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CURRENT_YEAR = new Date().getFullYear();

const YEAR_OPTIONS = Array.from(
  { length: 7 },
  (_, index) => CURRENT_YEAR - 3 + index,
);

const normalizeText = (value: unknown): string =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const getApiField = (item: any, ...keys: string[]): any => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) {
      return item[key];
    }
  }

  return undefined;
};

const parseApiBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
};

const extractApiRecords = (response: any): any[] => {
  // Axios responses may wrap the API payload in `data`; some API actions
  // return ServiceSearchResponse with `result`/`Result`; others return arrays.
  let result = response?.data ?? response ?? {};

  for (let depth = 0; depth < 4; depth += 1) {
    if (Array.isArray(result)) return result;

    const nested =
      result?.result ??
      result?.Result ??
      result?.items ??
      result?.Items ??
      result?.records ??
      result?.Records ??
      result?.data ??
      result?.Data;

    if (nested === undefined || nested === null || nested === result) break;
    result = nested;
  }

  return Array.isArray(result) ? result : [];
};

const formatDateOnly = (value: unknown): string => {
  const raw = String(value ?? "").trim();
  if (!raw) return "—";

  // ISO / SQL date-time values, e.g. 2026-10-23T08:09:43 or 2026-10-23 08:09:43.
  // Use the date portion only so no timezone conversion can change the day.
  const isoDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T|\s)/);
  if (isoDate) return `${isoDate[3]}-${isoDate[2]}-${isoDate[1]}`;

  // Backend/legacy values may already be formatted as dd-MM-yyyy or dd/MM/yyyy
  // with a time suffix. Return the date portion directly instead of letting
  // Date.parse fail and accidentally displaying the original time.
  const dayFirstDate = raw.match(
    /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:$|[ T])/,
  );
  if (dayFirstDate) {
    return `${dayFirstDate[1].padStart(2, "0")}-${dayFirstDate[2].padStart(
      2,
      "0",
    )}-${dayFirstDate[3]}`;
  }

  // Fallback for other parseable date strings.
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${date.getFullYear()}`;
};

const dateFilterMatches = (value: unknown, query: string): boolean => {
  const raw = String(value ?? "").trim();
  const search = query.trim().toLowerCase();
  if (!search) return true;

  const displayDate = formatDateOnly(raw);
  const searchable = `${displayDate} ${raw}`.toLowerCase();
  if (searchable.includes(search)) return true;

  // Also allow date searches with different separators/order:
  // dd-MM-yyyy matches the displayed date; yyyy-MM-dd matches the API value.
  const digitsOnly = (text: string) => text.replace(/[^0-9]/g, "");
  const normalizedSearch = digitsOnly(search);
  return Boolean(
    normalizedSearch &&
      (digitsOnly(displayDate).includes(normalizedSearch) ||
        digitsOnly(raw).includes(normalizedSearch)),
  );
};

const inputDateValue = (value: unknown): string => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateTime = (value: unknown): string => {
  const raw = String(value ?? "").trim();
  if (!raw) return "—";
  // SQL stores these audit timestamps as UTC datetime2 values. ASP.NET may
  // serialize DateTimeKind.Unspecified without a trailing Z, so mark that
  // representation as UTC before displaying in the supervisor's local time.
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const normalized =
    !hasTimezone && /^\d{4}-\d{2}-\d{2}T/.test(raw) ? `${raw}Z` : raw;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return raw;

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const time = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  return `${day}-${month}-${year} ${time}`;
};

const resolveEmployeeType = (shiftTypeValue: unknown): EmployeeType => {
  const shiftType = normalizeText(shiftTypeValue);

  if (shiftType.includes("apprentice")) return "cnc";
  if (shiftType.includes("contract")) return "contract";

  return "payroll";
};

const getShiftRowsKey = (subDepartmentKey: string, shiftKey: string): string =>
  `${subDepartmentKey}::${shiftKey}`;

const nextIsoDate = (value: string): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return "";
  const date = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return "";
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
};

const getTimestamp = (value: unknown): number => {
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  const normalized =
    !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw) && /^\d{4}-\d{2}-\d{2}T/.test(raw)
      ? `${raw}Z`
      : raw;
  const timestamp = new Date(normalized).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

interface EightHoursViewShiftPageProps {
  /** Render the roster within the Import page, without its own breadcrumb. */
  embedded?: boolean;
  /** Increment after a successful import to reload the current roster. */
  refreshKey?: number;
  /** Keep the roster aligned with the import's current filters. */
  selection?: {
    subDepartmentId?: string | number;
    employeeType?: EmployeeType;
    shiftId?: string | number;
    month?: number;
    year?: number;
  };
}

export default function EightHoursViewShiftPage({
  embedded = false,
  refreshKey = 0,
  selection,
}: EightHoursViewShiftPageProps = {}) {
  const navigate = useNavigate();

  // ---------------------------------------------------------
  // Selection state
  // ---------------------------------------------------------

  const [selectedSubDepartmentId, setSelectedSubDepartmentId] = useState("");

  const [employeeType, setEmployeeType] = useState<EmployeeType>("payroll");

  const [selectedShiftId, setSelectedShiftId] = useState("");

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // ---------------------------------------------------------
  // API data
  // ---------------------------------------------------------

  const [allSubDepartments, setAllSubDepartments] = useState<
    SubDepartmentConfig[]
  >([]);

  const [allShifts, setAllShifts] = useState<ShiftConfig[]>([]);

  /*
   * Roster keys use SubDepartmentId + ShiftMasterId.
   *
   * Example:
   * 5Axis::12
   * Bending::12
   *
   * These remain separate rosters even when they use the same shift.
   */
  const [shiftRows, setShiftRows] = useState<Record<string, ShiftEmployee[]>>(
    {},
  );

  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");
  // Preserve the API row count so an empty server response is not confused
  // with client-side filtering that hides valid records.
  const [apiRosterRecordCount, setApiRosterRecordCount] = useState(0);

  // Date-effective individual shift editing and audit history.
  const [editOpen, setEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<ShiftEmployee | null>(null);
  const [editShiftMasterId, setEditShiftMasterId] = useState("");
  const [editEffectiveFrom, setEditEffectiveFrom] = useState("");
  const [editEffectiveTo, setEditEffectiveTo] = useState("");
  const [editReason, setEditReason] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState<ShiftOverrideHistoryRow[]>([]);
  const [historyPerson, setHistoryPerson] = useState<ShiftEmployee | null>(
    null,
  );

  // ---------------------------------------------------------
  // Table state
  // ---------------------------------------------------------

  const [tableSearch, setTableSearch] = useState("");
  const [showAllFilters, setShowAllFilters] = useState(false);

  const [columnFilters, setColumnFilters] = useState<
    Partial<Record<keyof ShiftEmployee, string>>
  >({});

  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">("asc");

  const [valueToOrderBy, setValueToOrderBy] =
    useState<keyof ShiftEmployee>("personId");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  // The roster uses one tab per shift instead of combining shifts into one list.
  const [activeRosterShiftId, setActiveRosterShiftId] = useState("");

  // In merged mode, follow the selected schedule period from the Import section.
  // Standalone View Shift mode remains independently filterable.
  useEffect(() => {
    if (
      selection?.subDepartmentId != null &&
      String(selection.subDepartmentId).trim()
    ) {
      setSelectedSubDepartmentId(String(selection.subDepartmentId));
    }
    if (selection?.employeeType) setEmployeeType(selection.employeeType);
    if (selection?.shiftId != null && String(selection.shiftId).trim()) {
      const shiftId = String(selection.shiftId);
      setSelectedShiftId(shiftId);
      setActiveRosterShiftId(shiftId);
    }
    if (selection?.month != null && Number.isFinite(Number(selection.month))) {
      setSelectedMonth(Number(selection.month));
    }
    if (selection?.year != null && Number.isFinite(Number(selection.year))) {
      setSelectedYear(Number(selection.year));
    }
  }, [
    refreshKey,
    selection?.subDepartmentId,
    selection?.employeeType,
    selection?.shiftId,
    selection?.month,
    selection?.year,
  ]);

  // ---------------------------------------------------------
  // Derived selections
  // ---------------------------------------------------------

  const subDepartments = allSubDepartments;

  const selectedSubDepartment = useMemo(
    () => subDepartments.find((item) => item.id === selectedSubDepartmentId),
    [subDepartments, selectedSubDepartmentId],
  );

  const shifts = useMemo(
    () => allShifts.filter((shift) => shift.employeeType === employeeType),
    [allShifts, employeeType],
  );

  const selectedShift = useMemo(
    () => shifts.find((shift) => shift.id === selectedShiftId),
    [shifts, selectedShiftId],
  );

  const employeeTypeLabel =
    EMPLOYEE_TYPE_OPTIONS.find((item) => item.value === employeeType)?.label ??
    "Payroll";

  // ---------------------------------------------------------
  // Load current employee data
  // ---------------------------------------------------------

  const loadShiftDetails = useCallback(
    async (month: number = selectedMonth, year: number = selectedYear) => {
      setViewLoading(true);
      setViewError("");

      try {
        const [subDepartmentResponse, shiftResponse, rowsResponse] =
          await Promise.all([
            GetASAISubDepartments(),
            GetDepartmentShifts(8),
            GetCurrentShiftImportRecords({
              scheduleMonth: month,
              scheduleYear: year,
              shiftHours: 8,
            }),
          ]);

        const rawSubDepartments = extractApiRecords(subDepartmentResponse);

        const shiftRecords = extractApiRecords(shiftResponse);
        const currentRecords = extractApiRecords(rowsResponse);
        setApiRosterRecordCount(currentRecords.length);

        // -----------------------------------------------------
        // Normalize active 8-hour ShiftMaster records
        // -----------------------------------------------------

        const normalizedShifts: ShiftConfig[] = shiftRecords
          .map((item: any): ShiftConfig | null => {
            const rawId = getApiField(
              item,
              "id",
              "Id",
              "shiftMasterId",
              "ShiftMasterId",
            );

            const id = String(rawId ?? "").trim();

            const shiftType = String(
              getApiField(item, "shiftType", "ShiftType") ?? "",
            ).trim();

            const rawHours = getApiField(item, "shiftHours", "ShiftHours");

            const shiftName = String(
              getApiField(item, "shiftName", "ShiftName") ?? "",
            ).trim();

            const shiftNumber = String(
              getApiField(item, "shiftNumber", "ShiftNumber") ?? "",
            ).trim();

            if (
              !id ||
              (rawHours != null && Number(rawHours) !== 8) ||
              !shiftType
            ) {
              return null;
            }

            return {
              id,
              key: id,
              label: shiftName || `${shiftType} — Shift ${shiftNumber || id}`,
              shiftType,
              employeeType: resolveEmployeeType(shiftType),
            };
          })
          .filter((item): item is ShiftConfig => item !== null);

        // -----------------------------------------------------
        // Normalize active sub-departments
        // -----------------------------------------------------

        const normalizedSubDepartments: SubDepartmentConfig[] =
          rawSubDepartments
            .map((item: any): SubDepartmentConfig | null => {
              const rawId = getApiField(
                item,
                "id",
                "Id",
                "subDepartmentId",
                "SubDepartmentId",
              );

              const id = String(rawId ?? "").trim();

              const label = String(
                getApiField(
                  item,
                  "machineName",
                  "MachineName",
                  "subDepartmentName",
                  "SubDepartmentName",
                ) ?? "",
              ).trim();

              const code = String(
                getApiField(
                  item,
                  "machineCode",
                  "MachineCode",
                  "subDepartmentCode",
                  "SubDepartmentCode",
                ) ?? "",
              ).trim();

              if (!id || !label) return null;

              return {
                id,
                key: id,
                label,
                code,
              };
            })
            .filter((item): item is SubDepartmentConfig => item !== null);

        const uniqueSubDepartments = new Map<string, SubDepartmentConfig>();

        normalizedSubDepartments.forEach((item) => {
          uniqueSubDepartments.set(item.key, item);
        });

        // -----------------------------------------------------
        // Initialize all known sub-department/shift rosters
        // -----------------------------------------------------

        const grouped: Record<string, ShiftEmployee[]> = {};

        normalizedShifts.forEach((shift) => {
          uniqueSubDepartments.forEach((subDepartment) => {
            grouped[getShiftRowsKey(subDepartment.key, shift.key)] = [];
          });
        });

        // -----------------------------------------------------
        // Group effective roster records by BOTH keys.
        // If an older API deployment has not returned effective dates yet,
        // show the base monthly assignment as effective for the selected month.
        // The backend will return narrower segments when temporary overrides
        // are enabled.
        // -----------------------------------------------------

        const selectedMonthStart = `${year}-${String(month).padStart(
          2,
          "0",
        )}-01`;
        const selectedMonthEndDate = new Date(year, month, 0);
        const selectedMonthEnd =
          `${selectedMonthEndDate.getFullYear()}-` +
          `${String(selectedMonthEndDate.getMonth() + 1).padStart(2, "0")}-` +
          `${String(selectedMonthEndDate.getDate()).padStart(2, "0")}`;

        currentRecords.forEach((item: any) => {
          const shiftKey = String(
            getApiField(
              item,
              "effectiveShiftMasterId",
              "EffectiveShiftMasterId",
              "actualShiftMasterId",
              "ActualShiftMasterId",
              "shiftMasterId",
              "ShiftMasterId",
            ) ?? "",
          ).trim();

          // Ignore records for shifts that aren't returned by
          // the active department's ShiftMaster lookup.
          if (
            !shiftKey ||
            !normalizedShifts.some((shift) => shift.key === shiftKey)
          ) {
            return;
          }

          const rawSubDepartmentId = String(
            getApiField(item, "subDepartmentId", "SubDepartmentId") ?? "",
          ).trim();

          const subDepartmentName = String(
            getApiField(
              item,
              "subDepartmentName",
              "SubDepartmentName",
              "subDepartment",
              "SubDepartment",
              "machineName",
              "MachineName",
            ) ?? "",
          ).trim();

          const subDepartmentCode = String(
            getApiField(
              item,
              "subDepartmentCode",
              "SubDepartmentCode",
              "machineCode",
              "MachineCode",
            ) ?? "",
          ).trim();

          /*
           * Prefer SubDepartmentId. If the API returns no ID,
           * try matching the name to the active lookup.
           */
          const matchingSubDepartment = normalizedSubDepartments.find(
            (subDepartment) => {
              if (
                rawSubDepartmentId &&
                subDepartment.id === rawSubDepartmentId
              ) {
                return true;
              }

              return (
                !rawSubDepartmentId &&
                normalizeText(subDepartment.label) ===
                  normalizeText(subDepartmentName)
              );
            },
          );

          const subDepartmentKey =
            rawSubDepartmentId ||
            matchingSubDepartment?.key ||
            (subDepartmentName
              ? `name:${normalizeText(subDepartmentName)}`
              : "");

          if (!subDepartmentKey) return;

          /*
           * Preserve the sub-department for existing records even
           * if it was subsequently deactivated.
           */
          if (!uniqueSubDepartments.has(subDepartmentKey)) {
            uniqueSubDepartments.set(subDepartmentKey, {
              id: rawSubDepartmentId || subDepartmentKey,
              key: subDepartmentKey,
              label:
                subDepartmentName || `Sub-department ${rawSubDepartmentId}`,
              code: subDepartmentCode,
            });
          }

          const rowKey = getShiftRowsKey(subDepartmentKey, shiftKey);
          if (!grouped[rowKey]) grouped[rowKey] = [];

          const personId = String(
            getApiField(item, "personId", "PersonId", "Person_ID") ?? "",
          ).trim();
          const baseShiftMasterId = String(
            getApiField(item, "baseShiftMasterId", "BaseShiftMasterId") ??
              shiftKey,
          ).trim();
          const effectiveShiftMasterId = shiftKey;
          const effectiveFrom =
            inputDateValue(
              getApiField(item, "effectiveFrom", "EffectiveFrom"),
            ) || selectedMonthStart;
          const effectiveTo =
            inputDateValue(getApiField(item, "effectiveTo", "EffectiveTo")) ||
            selectedMonthEnd;
          const rawLastUpdatedAt = String(
            getApiField(
              item,
              "lastUpdatedAt",
              "LastUpdatedAt",
              "updatedAt",
              "UpdatedAt",
              "importedAt",
              "ImportedAt",
              "createdAt",
              "CreatedAt",
              "updatedDate",
              "UpdatedDate",
              "createdDate",
              "CreatedDate",
            ) ?? "",
          );

          // A same-as-base assignment is not a temporary shift, even if an
          // older backend has left a redundant override row in the database.
          // Normalize it here and merge adjacent intervals so the UI shows the
          // employee's ordinary monthly assignment as one row.
          const isTemporaryOverride =
            parseApiBoolean(
              getApiField(item, "isTemporaryOverride", "IsTemporaryOverride"),
            ) && effectiveShiftMasterId !== baseShiftMasterId;

          const row: ShiftEmployee = {
            personId,
            personName: String(
              getApiField(item, "personName", "PersonName", "Person_Name") ??
                "",
            ),
            shiftName: String(
              getApiField(item, "shiftName", "ShiftName") ??
                normalizedShifts.find((shift) => shift.id === shiftKey)
                  ?.label ??
                "",
            ),
            subDepartmentName:
              subDepartmentName || matchingSubDepartment?.label || "",
            subDepartmentCode,
            departmentId: Number(
              getApiField(item, "departmentId", "DepartmentId") ?? 0,
            ),
            subDepartmentId: Number(
              getApiField(item, "subDepartmentId", "SubDepartmentId") ??
                rawSubDepartmentId ??
                0,
            ),
            offDay: String(
              getApiField(item, "offDay", "OffDay", "Off_day") ?? "",
            ),
            shiftMasterId: effectiveShiftMasterId,
            baseShiftMasterId,
            baseShiftImportRecordId: Number(
              getApiField(
                item,
                "baseShiftImportRecordId",
                "BaseShiftImportRecordId",
                "shiftImportRecordId",
                "ShiftImportRecordId",
              ) ?? 0,
            ),
            effectiveFrom,
            effectiveTo,
            lastUpdatedAt: rawLastUpdatedAt,
            isTemporaryOverride,
          };

          const rowsForShift = grouped[rowKey];
          const previous = rowsForShift[rowsForShift.length - 1];
          const canMergeWithPrevious = Boolean(
            previous &&
              !previous.isTemporaryOverride &&
              !row.isTemporaryOverride &&
              previous.personId === row.personId &&
              previous.baseShiftImportRecordId ===
                row.baseShiftImportRecordId &&
              previous.baseShiftMasterId === row.baseShiftMasterId &&
              previous.shiftMasterId === row.shiftMasterId &&
              previous.subDepartmentId === row.subDepartmentId &&
              previous.effectiveTo &&
              row.effectiveFrom &&
              nextIsoDate(previous.effectiveTo) === row.effectiveFrom,
          );

          if (canMergeWithPrevious && previous) {
            previous.effectiveTo = row.effectiveTo;
            if (
              getTimestamp(row.lastUpdatedAt) >
              getTimestamp(previous.lastUpdatedAt)
            ) {
              previous.lastUpdatedAt = row.lastUpdatedAt;
            }
          } else {
            rowsForShift.push(row);
          }
        });

        const sortedSubDepartments = Array.from(
          uniqueSubDepartments.values(),
        ).sort((a, b) =>
          a.label.localeCompare(b.label, undefined, {
            sensitivity: "base",
          }),
        );

        setAllShifts(normalizedShifts);
        setAllSubDepartments(sortedSubDepartments);
        setShiftRows(grouped);
      } catch (error) {
        console.error("Failed to load current shift import records:", error);

        setAllSubDepartments([]);
        setAllShifts([]);
        setShiftRows({});
        setApiRosterRecordCount(0);

        setViewError(
          "Unable to load current employees. Please verify the sub-department, shift and current-record APIs.",
        );
      } finally {
        setViewLoading(false);
      }
    },
    [selectedMonth, selectedYear],
  );

  // Reload current records when the requested schedule period changes.
  useEffect(() => {
    void loadShiftDetails();
  }, [loadShiftDetails, refreshKey]);

  // Choose the first available sub-department if the current
  // selection is no longer present.
  useEffect(() => {
    if (subDepartments.length === 0) {
      if (selectedSubDepartmentId) {
        setSelectedSubDepartmentId("");
      }
      return;
    }

    const exists = subDepartments.some(
      (item) => item.id === selectedSubDepartmentId,
    );

    if (!exists) {
      setSelectedSubDepartmentId(subDepartments[0].id);
    }
  }, [subDepartments, selectedSubDepartmentId]);

  // ---------------------------------------------------------
  // Selected roster
  // ---------------------------------------------------------

  const currentRows = useMemo(() => {
    // The embedded Import page displays every shift across all sub-departments.
    if (embedded) {
      return Object.values(shiftRows).flat();
    }

    // Standalone View Shift keeps the selected sub-department and employee type,
    // but loads every shift in that scope so the roster can be navigated by tabs.
    if (!selectedSubDepartment) return [];

    return shifts.flatMap((shift) => {
      const key = getShiftRowsKey(selectedSubDepartment.key, shift.key);
      return shiftRows[key] ?? [];
    });
  }, [embedded, selectedSubDepartment, shifts, shiftRows]);

  // ---------------------------------------------------------
  // Shift-wise roster tabs (Import and standalone View Shift)
  // ---------------------------------------------------------

  const rosterShiftTabs = useMemo<ShiftRosterTab[]>(() => {
    const byShift = new Map<string, ShiftRosterTab>();

    currentRows.forEach((row) => {
      const id = String(row.shiftMasterId || "").trim();
      if (!id) return;

      const existing = byShift.get(id);
      if (existing) {
        existing.count += 1;
      } else {
        const configuredShift = allShifts.find((shift) => shift.id === id);
        byShift.set(id, {
          id,
          label:
            row.shiftName?.trim() || configuredShift?.label || `Shift ${id}`,
          count: 1,
        });
      }
    });

    return Array.from(byShift.values()).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
  }, [currentRows, allShifts]);

  const activeRosterShift = useMemo(
    () =>
      rosterShiftTabs.find((shift) => shift.id === activeRosterShiftId) ??
      rosterShiftTabs[0] ??
      null,
    [rosterShiftTabs, activeRosterShiftId],
  );

  // Keep the active tab valid when employee type, sub-department, or month changes.
  // The first populated shift is selected automatically when the old tab disappears.
  useEffect(() => {
    if (rosterShiftTabs.length === 0) {
      if (activeRosterShiftId) setActiveRosterShiftId("");
      if (selectedShiftId) setSelectedShiftId("");
      return;
    }

    const nextShiftId = rosterShiftTabs.some(
      (shift) => shift.id === activeRosterShiftId,
    )
      ? activeRosterShiftId
      : rosterShiftTabs[0].id;

    if (nextShiftId !== activeRosterShiftId) {
      setActiveRosterShiftId(nextShiftId);
      setPage(0);
      setColumnFilters({});
    }
    if (nextShiftId !== selectedShiftId) {
      setSelectedShiftId(nextShiftId);
    }
  }, [rosterShiftTabs, activeRosterShiftId, selectedShiftId]);

  const visibleRows = useMemo(() => {
    if (!activeRosterShift) return [];

    return currentRows.filter(
      (row) => String(row.shiftMasterId) === activeRosterShift.id,
    );
  }, [currentRows, activeRosterShift]);

  // ---------------------------------------------------------
  // Search and column filters
  // ---------------------------------------------------------

  const hasColumnFilters = Object.values(columnFilters).some(
    (value) =>
      value !== null && value !== undefined && String(value).trim() !== "",
  );

  const filteredRows = useMemo(() => {
    let data = [...visibleRows];

    const globalSearch = tableSearch.trim().toLowerCase();

    if (globalSearch) {
      data = data.filter((row) =>
        `${row.personId} ${row.personName} ${row.shiftName} ${row.subDepartmentName} ${row.subDepartmentCode} ${row.offDay}`
          .toLowerCase()
          .includes(globalSearch),
      );
    }

    Object.entries(columnFilters).forEach(([field, value]) => {
      if (!value?.trim()) return;

      const searchValue = value.trim().toLowerCase();

      data = data.filter((row) => {
        const typedField = field as keyof ShiftEmployee;
        if (
          typedField === "effectiveFrom" ||
          typedField === "effectiveTo" ||
          typedField === "lastUpdatedAt"
        ) {
          return dateFilterMatches(row[typedField], value);
        }

        return String(row[typedField] ?? "")
          .toLowerCase()
          .includes(searchValue);
      });
    });

    return [...data].sort((a, b) => {
      const aValue = String(a[valueToOrderBy] ?? "").toLowerCase();

      const bValue = String(b[valueToOrderBy] ?? "").toLowerCase();

      const comparison = aValue.localeCompare(bValue, undefined, {
        numeric: true,
        sensitivity: "base",
      });

      return orderDirection === "asc" ? comparison : -comparison;
    });
  }, [visibleRows, tableSearch, columnFilters, valueToOrderBy, orderDirection]);

  const pagedRows = useMemo(
    () =>
      rowsPerPage === -1
        ? filteredRows
        : filteredRows.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage,
          ),
    [filteredRows, page, rowsPerPage],
  );

  useEffect(() => {
    const maxPage =
      rowsPerPage === -1
        ? 0
        : Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1);

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredRows.length, page, rowsPerPage]);

  const openEditDialog = (row: ShiftEmployee) => {
    setEditingRow(row);
    setEditShiftMasterId(String(row.shiftMasterId || selectedShiftId));

    const monthStart = `${selectedYear}-${String(selectedMonth).padStart(
      2,
      "0",
    )}-01`;
    const monthEndDate = new Date(selectedYear, selectedMonth, 0);
    const monthEndText = `${monthEndDate.getFullYear()}-${String(
      monthEndDate.getMonth() + 1,
    ).padStart(2, "0")}-${String(monthEndDate.getDate()).padStart(2, "0")}`;

    // Safety default: editing a normal monthly assignment starts as a ONE-DAY
    // change at the beginning of the displayed segment. This avoids accidentally
    // changing the whole month just by selecting another shift. For an existing
    // temporary segment, keep its current range so HR can revise it deliberately.
    const rowStart =
      row.effectiveFrom >= monthStart && row.effectiveFrom <= monthEndText
        ? row.effectiveFrom
        : monthStart;
    const rowEnd =
      row.effectiveTo >= rowStart && row.effectiveTo <= monthEndText
        ? row.effectiveTo
        : rowStart;
    setEditEffectiveFrom(rowStart);
    setEditEffectiveTo(row.isTemporaryOverride ? rowEnd : rowStart);
    setEditReason("");
    setViewError("");
    setEditOpen(true);
  };

  const saveEmployeeShiftOverride = async () => {
    if (!editingRow) return;
    if (!editingRow.baseShiftImportRecordId) {
      setViewError(
        "The roster API did not return BaseShiftImportRecordId. Update GetCurrentShiftImportRecords before editing.",
      );
      return;
    }
    if (!editShiftMasterId || !editEffectiveFrom || !editEffectiveTo) {
      setViewError("Select a shift and both effective dates.");
      return;
    }
    if (editEffectiveFrom > editEffectiveTo) {
      setViewError("Effective From cannot be later than Effective To.");
      return;
    }
    const monthStart = `${selectedYear}-${String(selectedMonth).padStart(
      2,
      "0",
    )}-01`;
    const endDate = new Date(selectedYear, selectedMonth, 0);
    const monthEnd = `${endDate.getFullYear()}-${String(
      endDate.getMonth() + 1,
    ).padStart(2, "0")}-${String(endDate.getDate()).padStart(2, "0")}`;
    if (editEffectiveFrom < monthStart || editEffectiveTo > monthEnd) {
      setViewError(
        `Effective dates must be inside ${
          MONTH_OPTIONS[selectedMonth - 1]
        } ${selectedYear}.`,
      );
      return;
    }

    setSavingEdit(true);
    setViewError("");
    try {
      const response = await SaveEmployeeShiftOverride({
        departmentId: editingRow.departmentId,
        personId: editingRow.personId,
        baseShiftImportRecordId: editingRow.baseShiftImportRecordId,
        subDepartmentId: editingRow.subDepartmentId,
        scheduleMonth: selectedMonth,
        scheduleYear: selectedYear,
        shiftHours: 8,
        employeeType,
        toShiftMasterId: Number(editShiftMasterId),
        effectiveFrom: editEffectiveFrom,
        effectiveTo: editEffectiveTo,
        changeReason: editReason.trim() || null,
      });
      const result = response?.data?.data ?? response?.data ?? response;
      const success = result?.success ?? result?.Success ?? true;
      if (!success)
        throw new Error(
          result?.message ??
            result?.Message ??
            "Shift change could not be saved.",
        );
      setEditOpen(false);
      setEditingRow(null);
      await loadShiftDetails(selectedMonth, selectedYear);
    } catch (error: any) {
      console.error("Failed to save employee shift override:", error);
      setViewError(
        error?.response?.data?.message ??
          error?.response?.data?.Message ??
          error?.response?.data ??
          error?.message ??
          "Unable to save the employee shift change.",
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const openHistoryDialog = async (row: ShiftEmployee) => {
    setHistoryPerson(row);
    setHistoryRows([]);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const response = await GetEmployeeShiftOverrideHistory({
        departmentId: row.departmentId,
        personId: row.personId,
        baseShiftImportRecordId: row.baseShiftImportRecordId,
        scheduleMonth: selectedMonth,
        scheduleYear: selectedYear,
        subDepartmentId: row.subDepartmentId,
      });
      setHistoryRows(extractApiRecords(response) as ShiftOverrideHistoryRow[]);
    } catch (error) {
      console.error("Failed to load shift change history:", error);
      setViewError(
        "Unable to load employee shift history. Verify the GetEmployeeShiftOverrideHistory endpoint.",
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  // ---------------------------------------------------------
  // Table helpers
  // ---------------------------------------------------------

  const resetTableFilters = () => {
    setTableSearch("");
    setColumnFilters({});
    setShowAllFilters(false);
    setPage(0);
  };

  const handleSelectionChange = (action: () => void) => {
    action();
    resetTableFilters();
  };

  const handleSort = (field: keyof ShiftEmployee) => {
    const isAsc = valueToOrderBy === field && orderDirection === "asc";

    setOrderDirection(isAsc ? "desc" : "asc");
    setValueToOrderBy(field);
    setPage(0);
  };

  const clearColumnFilter = (field: keyof ShiftEmployee) => {
    setColumnFilters((previous) => {
      const updated = { ...previous };
      delete updated[field];
      return updated;
    });

    setPage(0);
  };

  const isColumnFiltered = (field: keyof ShiftEmployee) =>
    Boolean(columnFilters[field]?.trim());

  const totalEmployees = useMemo(
    () =>
      Object.values(shiftRows).reduce((total, rows) => total + rows.length, 0),
    [shiftRows],
  );

  // ---------------------------------------------------------
  // Page title and breadcrumbs
  // ---------------------------------------------------------

  const selectedViewTitle = `${
    MONTH_OPTIONS[selectedMonth - 1]
  } ${selectedYear} ${employeeTypeLabel} Shift Details`;

  const breadcrumbItems = [
    {
      to: "/",
      title: "ASAI",
    },
    {
      title: "8 Hours Shift",
    },
    {
      title: "View Shift",
    },
    {
      title: employeeTypeLabel,
    },
    ...(selectedSubDepartment ? [{ title: selectedSubDepartment.label }] : []),
    ...(selectedShift ? [{ title: selectedShift.label }] : []),
  ];

  // ---------------------------------------------------------
  // Render — styled to match the Shift Master page
  // ---------------------------------------------------------

  const rosterContent = (
    <Box>
      {/* =================================================
            CURRENT EMPLOYEES — MATCH SHIFT MASTER LIST CARD
        ================================================= */}
      <Card
        sx={{
          mt: 0,
          p: 0,
          overflow: "hidden",
        }}
      >
        {/* LIST HEADER + SEARCH / FILTER */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1.5}
          px={2}
          py={1.5}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <Box>
            <Typography>Current Employees</Typography>
            <Typography
              sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}
            >
              {embedded
                ? "All sub-departments"
                : selectedSubDepartment?.label ?? "Sub-department"}
              {" · "}
              {activeRosterShift?.label ?? "Shift"}
              {" · "}
              {MONTH_OPTIONS[selectedMonth - 1]} {selectedYear}
              {" · "}
              {filteredRows.length} employees
            </Typography>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            flexWrap="wrap"
            gap={1}
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            <TextField
              size="small"
              placeholder="Search..."
              value={tableSearch}
              onFocus={() => {
                setShowAllFilters(false);
                setColumnFilters({});
              }}
              onChange={(event) => {
                setTableSearch(event.target.value);
                setPage(0);
              }}
              sx={{
                width: { xs: "100%", sm: 220 },
                minWidth: { sm: 180 },
                backgroundColor: "white",
                borderRadius: 1,
                "& .MuiInputBase-root": {
                  height: 34,
                  fontSize: 13,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 1 }}>
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: tableSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => {
                        setTableSearch("");
                        setPage(0);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />

            <Tooltip
              title={
                showAllFilters ? "Hide column filters" : "Show column filters"
              }
            >
              <IconButton
                size="small"
                onClick={() => {
                  setShowAllFilters((previous) => !previous);
                  setPage(0);
                }}
                sx={{
                  height: 34,
                  width: 34,
                  ml: 0.25,
                  color: showAllFilters ? "primary.main" : "text.primary",
                }}
              >
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Button
              variant="outlined"
              size="small"
              sx={{
                height: 32,
                whiteSpace: "nowrap",
                textTransform: "none",
              }}
              onClick={resetTableFilters}
            >
              Reset Filter
            </Button>
          </Box>
        </Box>

        <Box sx={{ px: 2, pt: 2, pb: 0 }}>
          {viewError && (
            <Alert
              severity="error"
              sx={{ mb: 1.5 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={viewLoading}
                  onClick={() => void loadShiftDetails()}
                >
                  Retry
                </Button>
              }
            >
              {viewError}
            </Alert>
          )}

          {!embedded && (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "1.3fr 1fr 0.9fr 0.7fr auto",
                  },
                  gap: 1.5,
                  alignItems: "start",
                }}
              >
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Sub-department"
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 260,
                        },
                      },
                    },
                  }}
                  value={selectedSubDepartmentId}
                  onChange={(event) => {
                    handleSelectionChange(() =>
                      setSelectedSubDepartmentId(event.target.value),
                    );
                  }}
                  disabled={allSubDepartments.length === 0}
                >
                  {allSubDepartments.map((subDepartment) => (
                    <MenuItem key={subDepartment.key} value={subDepartment.id}>
                      {subDepartment.label}
                      {subDepartment.code ? ` (${subDepartment.code})` : ""}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Employee Type"
                  value={employeeType}
                  onChange={(event) => {
                    const value = event.target.value as EmployeeType;
                    handleSelectionChange(() => setEmployeeType(value));
                  }}
                >
                  {EMPLOYEE_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Month"
                  value={selectedMonth}
                  onChange={(event) => {
                    handleSelectionChange(() =>
                      setSelectedMonth(Number(event.target.value)),
                    );
                  }}
                >
                  {MONTH_OPTIONS.map((month, index) => (
                    <MenuItem key={month} value={index + 1}>
                      {month}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Year"
                  value={selectedYear}
                  onChange={(event) => {
                    handleSelectionChange(() =>
                      setSelectedYear(Number(event.target.value)),
                    );
                  }}
                >
                  {YEAR_OPTIONS.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>

                <Button
                  variant="outlined"
                  size="small"
                  disabled={viewLoading}
                  onClick={() => void loadShiftDetails()}
                  sx={{
                    height: 40,
                    px: 1.75,
                    whiteSpace: "nowrap",
                    textTransform: "none",
                    alignSelf: { xs: "stretch", lg: "end" },
                  }}
                >
                  Refresh
                </Button>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
              >
                {!embedded && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() =>
                      navigate(routes.eightHoursAddEditShift.match)
                    }
                    sx={{
                      textTransform: "none",
                      boxShadow: "none",
                    }}
                  >
                    Import Shift
                  </Button>
                )}
              </Box>
            </>
          )}
        </Box>

        <Box sx={{ p: 2 }}>
          {rosterShiftTabs.length > 0 && (
            <Box
              sx={{
                mb: 2,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Tabs
                value={activeRosterShift?.id ?? rosterShiftTabs[0].id}
                onChange={(_, value) => {
                  const nextShiftId = String(value);
                  setActiveRosterShiftId(nextShiftId);
                  setSelectedShiftId(nextShiftId);
                  setPage(0);
                  setColumnFilters({});
                }}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="View employees by shift"
                sx={{
                  minHeight: 42,
                  "& .MuiTab-root": {
                    minHeight: 42,
                    px: 2,
                    textTransform: "none",
                    fontSize: 13,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  },
                }}
              >
                {rosterShiftTabs.map((shift) => (
                  <Tab
                    key={shift.id}
                    value={shift.id}
                    label={`${shift.label} (${shift.count})`}
                  />
                ))}
              </Tabs>
            </Box>
          )}

          {viewLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 140,
                gap: 1.5,
              }}
            >
              <CircularProgress size={26} />
              <Typography fontSize={13} color="text.secondary">
                Loading employees...
              </Typography>
            </Box>
          ) : filteredRows.length === 0 &&
            !tableSearch.trim() &&
            !hasColumnFilters ? (
            apiRosterRecordCount === 0 ? (
              <Alert severity="info" sx={{ mt: 0.5 }}>
                No roster records were returned by the server for{" "}
                {MONTH_OPTIONS[selectedMonth - 1]} {selectedYear}. The page
                received an empty result, so there are no employee rows to
                display or edit. If employees should be assigned, the roster
                API/data needs to be checked.
              </Alert>
            ) : rosterShiftTabs.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 1 }}>
                No shifts with current employees were found for this selection.
              </Typography>
            ) : (
              <Typography color="text.secondary" sx={{ py: 1 }}>
                No current employees found for the selected shift. The roster
                service returned {apiRosterRecordCount} record(s), but none
                matched the active tab.
              </Typography>
            )
          ) : (
            <>
              <TableContainer
                component={Paper}
                sx={{
                  width: "100%",
                  height: showAllFilters ? 420 : 378,
                  maxHeight: showAllFilters ? 420 : 378,
                  overflowX: "auto",
                  overflowY: "auto",
                  // The viewport remains sized for five rows; larger page
                  // sizes and All scroll inside this container.
                }}
              >
                <Table
                  sx={{
                    minWidth: 1000,
                    "& .MuiTableCell-root": {
                      whiteSpace: "nowrap",
                      verticalAlign: "middle",
                    },
                  }}
                >
                  <TableHead>
                    {/* MAIN HEADER — SAME AS SHIFT MASTER */}
                    <TableRow
                      sx={{
                        backgroundColor: "#FAFBFB",
                        "& .MuiTableCell-root": {
                          position: "sticky",
                          top: 0,
                          zIndex: 4,
                          height: 56,
                          boxSizing: "border-box",
                          backgroundColor: "#FAFBFB",
                        },
                      }}
                    >
                      {(
                        [
                          ["Person ID", "personId"],
                          ["Person Name", "personName"],
                          ["Shift", "shiftName"],
                          ["Sub-department", "subDepartmentName"],
                          ["Off Day", "offDay"],
                        ] as Array<[string, keyof ShiftEmployee]>
                      ).map(([label, field]) => (
                        <TableCell key={field}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <TableSortLabel
                              active={valueToOrderBy === field}
                              direction={
                                valueToOrderBy === field
                                  ? orderDirection
                                  : "asc"
                              }
                              onClick={() => handleSort(field)}
                            >
                              {label}
                            </TableSortLabel>
                          </Box>
                        </TableCell>
                      ))}
                      <TableCell>Effective From</TableCell>
                      <TableCell>Effective To</TableCell>
                      <TableCell>Last Updated</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>

                    {/* FILTER ROW — SAME DENSITY AS SHIFT MASTER */}
                    {showAllFilters && (
                      <TableRow
                        sx={{
                          backgroundColor: "#FFFFFF",
                          "& .MuiTableCell-root": {
                            position: "sticky",
                            top: 56,
                            zIndex: 3,
                            padding: "6px 8px",
                            height: 42,
                            boxSizing: "border-box",
                            verticalAlign: "middle",
                            backgroundColor: "#FFFFFF",
                          },
                        }}
                      >
                        {(
                          [
                            ["Person ID", "personId"],
                            ["Person Name", "personName"],
                            ["Shift", "shiftName"],
                            ["Sub-department", "subDepartmentName"],
                            ["Off Day", "offDay"],
                            ["Effective From", "effectiveFrom"],
                            ["Effective To", "effectiveTo"],
                            ["Last Updated", "lastUpdatedAt"],
                          ] as Array<[string, keyof ShiftEmployee]>
                        ).map(([label, field]) => (
                          <TableCell key={field}>
                            <TextField
                              size="small"
                              fullWidth
                              value={columnFilters[field] || ""}
                              onChange={(event) => {
                                const value = event.target.value;
                                setColumnFilters((previous) => {
                                  const updated = { ...previous };
                                  if (value.trim()) {
                                    updated[field] = value;
                                  } else {
                                    delete updated[field];
                                  }
                                  return updated;
                                });
                                setPage(0);
                              }}
                              placeholder="Search..."
                              sx={{
                                minWidth: 90,
                                "& .MuiInputBase-root": {
                                  height: 32,
                                  fontSize: 13,
                                },
                                "& .MuiInputBase-input": {
                                  padding: "6px 8px",
                                },
                              }}
                              InputProps={{
                                endAdornment: columnFilters[field] ? (
                                  <InputAdornment position="end">
                                    <IconButton
                                      size="small"
                                      aria-label={`Clear ${label} filter`}
                                      onClick={() => clearColumnFilter(field)}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                ) : null,
                              }}
                            />
                          </TableCell>
                        ))}
                        <TableCell />
                      </TableRow>
                    )}
                  </TableHead>

                  <TableBody>
                    {pagedRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          align="center"
                          sx={{ py: 3, color: "text.secondary" }}
                        >
                          No matching employee records found. Adjust or reset
                          your filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      pagedRows.map((row, index) => (
                        <TableRow key={`${row.personId}-${index}`} hover>
                          <TableCell>{row.personId || "-"}</TableCell>
                          <TableCell>{row.personName || "-"}</TableCell>
                          <TableCell>
                            {row.shiftName || "-"}
                            {row.isTemporaryOverride ? (
                              <Typography
                                component="span"
                                color="primary.main"
                                sx={{ ml: 0.75, fontSize: 11, fontWeight: 600 }}
                              >
                                Temporary
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {row.subDepartmentName || "-"}
                            {row.subDepartmentCode
                              ? ` (${row.subDepartmentCode})`
                              : ""}
                          </TableCell>
                          <TableCell>{row.offDay || "-"}</TableCell>
                          <TableCell>
                            {formatDateOnly(row.effectiveFrom)}
                          </TableCell>
                          <TableCell>
                            {formatDateOnly(row.effectiveTo)}
                          </TableCell>
                          <TableCell>
                            {formatDateOnly(row.lastUpdatedAt)}
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              display="flex"
                              justifyContent="flex-end"
                              gap={0.5}
                            >
                              <Tooltip title="Edit shift and effective dates">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => openEditDialog(row)}
                                  aria-label={`Edit shift for ${
                                    row.personName || row.personId
                                  }`}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Shift change history">
                                <IconButton
                                  size="small"
                                  onClick={() => void openHistoryDialog(row)}
                                  aria-label={`Shift history for ${
                                    row.personName || row.personId
                                  }`}
                                >
                                  <HistoryOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                component="div"
                count={filteredRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(Number.parseInt(event.target.value, 10));
                  setPage(0);
                }}
                showFirstButton
                showLastButton
              />
            </>
          )}
        </Box>
      </Card>

      <Dialog
        open={editOpen}
        onClose={() => !savingEdit && setEditOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Employee Shift</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }} color="text.secondary" fontSize={13}>
            {editingRow?.personName || "Employee"} ({editingRow?.personId}) ·{" "}
            {MONTH_OPTIONS[selectedMonth - 1]} {selectedYear}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            }}
          >
            <TextField
              select
              fullWidth
              label="Shift"
              value={editShiftMasterId}
              onChange={(event) => setEditShiftMasterId(event.target.value)}
            >
              {shifts.map((shift) => (
                <MenuItem key={shift.id} value={shift.id}>
                  {shift.label}
                </MenuItem>
              ))}
            </TextField>
            <Box />
            <TextField
              type="date"
              fullWidth
              label="Effective From"
              value={editEffectiveFrom}
              onChange={(event) => setEditEffectiveFrom(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: `${selectedYear}-${String(selectedMonth).padStart(
                  2,
                  "0",
                )}-01`,
                max: `${selectedYear}-${String(selectedMonth).padStart(
                  2,
                  "0",
                )}-${new Date(selectedYear, selectedMonth, 0).getDate()}`,
              }}
            />
            <TextField
              type="date"
              fullWidth
              label="Effective To"
              value={editEffectiveTo}
              onChange={(event) => setEditEffectiveTo(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min:
                  editEffectiveFrom ||
                  `${selectedYear}-${String(selectedMonth).padStart(
                    2,
                    "0",
                  )}-01`,
                max: `${selectedYear}-${String(selectedMonth).padStart(
                  2,
                  "0",
                )}-${new Date(selectedYear, selectedMonth, 0).getDate()}`,
              }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Reason (optional)"
              value={editReason}
              onChange={(event) => setEditReason(event.target.value)}
              sx={{ gridColumn: { sm: "1 / -1" } }}
            />
          </Box>
          {editingRow && editShiftMasterId === editingRow.baseShiftMasterId ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              You selected this employee's original monthly shift. Saving will
              restore the normal shift for these dates and clear any temporary
              shift override within the selected period. The change will remain
              visible in history.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              The selected shift applies only between the chosen dates. Outside
              this period, the employee remains on their normal monthly shift.
              Previous changes are retained in history.
            </Alert>
          )}
          {viewError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {viewError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditOpen(false)} disabled={savingEdit}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void saveEmployeeShiftOverride()}
            disabled={
              savingEdit ||
              !editingRow ||
              !editShiftMasterId ||
              !editEffectiveFrom ||
              !editEffectiveTo
            }
            startIcon={
              savingEdit ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <EditOutlinedIcon />
              )
            }
          >
            {editingRow && editShiftMasterId === editingRow.baseShiftMasterId
              ? "Restore Original Shift"
              : "Save Shift Change"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Shift Change History</DialogTitle>
        <DialogContent dividers>
          <Typography color="text.secondary" fontSize={13} sx={{ mb: 2 }}>
            {historyPerson?.personName || "Employee"} ({historyPerson?.personId}
            ) · {MONTH_OPTIONS[selectedMonth - 1]} {selectedYear}
          </Typography>
          {historyLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 100,
                gap: 1,
              }}
            >
              <CircularProgress size={22} />
              <Typography>Loading history…</Typography>
            </Box>
          ) : historyRows.length === 0 ? (
            <Typography color="text.secondary">
              No shift override history was found for this employee in the
              selected month.
            </Typography>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ maxHeight: 420, overflow: "auto" }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>From Shift</TableCell>
                    <TableCell>Shift Applied</TableCell>
                    <TableCell>Effective From</TableCell>
                    <TableCell>Effective To</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell>Updated By</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyRows.map((item, index) => (
                    <TableRow key={item.shiftAssignmentOverrideId ?? index}>
                      <TableCell>{item.baseShiftName || "—"}</TableCell>
                      <TableCell>{item.overrideShiftName || "—"}</TableCell>
                      <TableCell>
                        {formatDateOnly(item.effectiveFrom)}
                      </TableCell>
                      <TableCell>{formatDateOnly(item.effectiveTo)}</TableCell>
                      <TableCell>{item.recordStatus || "—"}</TableCell>
                      <TableCell>
                        {formatDateTime(item.updatedAt || item.createdAt)}
                      </TableCell>
                      <TableCell>
                        {item.updatedByLogin || item.createdByLogin || "—"}
                      </TableCell>
                      <TableCell>{item.changeReason || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (embedded) return rosterContent;

  return (
    <PageContainer title={selectedViewTitle}>
      <Breadcrumb title={selectedViewTitle} items={breadcrumbItems} />
      {rosterContent}
    </PageContainer>
  );
}
